import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

const Header = ({ topBar = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 30);
      // Hide on scroll down (past 120px), show on scroll up
      if (y > 120 && y > lastY) {
        setHidden(true);
      } else if (y < lastY) {
        setHidden(false);
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  return (
    <header
      className={`glass-effect fixed top-0 left-0 right-0 z-50 border-b border-[#E2E8F0] transition-all duration-300 ${scrolled ? 'shadow-md' : ''} ${hidden ? '-translate-y-full' : 'translate-y-0'}`}
      data-testid="main-header"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {topBar}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 transition-all duration-300 ${scrolled ? 'py-1.5' : 'py-2.5'}`}>
        <div className="flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_school-legacy/artifacts/lt30qar7_PHOTO-2024-09-19-11-44-58.jpg"
              alt="Anjal Al-tarbyh Educational"
              className={`object-contain rounded transition-all duration-300 shrink-0 ${scrolled ? 'h-8 w-8' : 'h-9 w-9 sm:h-10 sm:w-10'}`}
            />
            <div className="min-w-0">
              <h2
                className={`font-bold text-[#0B132B] leading-tight truncate transition-all duration-300 ${scrolled ? 'text-[11px] sm:text-sm' : 'text-xs sm:text-base'}`}
                style={{ fontFamily: 'Cormorant Garamond' }}
              >
                {t('header.schoolName')}
              </h2>
              <p className={`text-[#475569] truncate leading-tight transition-all duration-300 ${scrolled ? 'text-[9px] sm:text-[10px]' : 'text-[10px] sm:text-xs'}`}>
                {t('header.alumniAssociation')}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-home">{t('header.home')}</Link>
            <Link to="/programs" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-programs">{t('header.programs')}</Link>
            <Link to="/directory" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-directory">{t('header.directory')}</Link>
            <Link to="/news" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-news">{t('header.news')}</Link>
            <Link to="/events" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-events">{t('header.events')}</Link>
            <Link to="/contact" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors" data-testid="nav-contact">{t('header.contact')}</Link>
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-[#0B132B] hover:text-[#1E3A8A] font-medium transition-colors"
              data-testid="language-toggle"
            >
              <Globe size={18} />
              {i18n.language === 'en' ? 'ع' : 'EN'}
            </button>
            <Link to="/register" className="btn-accent" data-testid="nav-register">{t('header.register')}</Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-[#0B132B]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-button"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4" data-testid="mobile-menu">
            <Link to="/" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.home')}</Link>
            <Link to="/programs" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.programs')}</Link>
            <Link to="/directory" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.directory')}</Link>
            <Link to="/news" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.news')}</Link>
            <Link to="/events" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.events')}</Link>
            <Link to="/contact" className="text-[#0B132B] hover:text-[#1E3A8A] font-medium" onClick={() => setIsOpen(false)}>{t('header.contact')}</Link>
            <button
              onClick={() => { toggleLanguage(); setIsOpen(false); }}
              className="flex items-center gap-2 text-[#0B132B] hover:text-[#1E3A8A] font-medium"
            >
              <Globe size={18} />
              {i18n.language === 'en' ? 'العربية' : 'English'}
            </button>
            <Link to="/register" className="btn-accent inline-block text-center" onClick={() => setIsOpen(false)}>{t('header.register')}</Link>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
